import { useEffect, useRef, useState } from 'react';
import { usePulseStore } from '../hooks/usePulseStore';
import { usePulsePlayback } from '../hooks/usePulsePlayback';
import { PulseCell } from './PulseCell';
import { MIN_CELL_WIDTH } from '../constants';
import type { PulseGridConfig, PulseContentItem } from '../types';

interface PulseGridProps {
  config: PulseGridConfig;
}

/**
 * Dense grid with orientation-aware tile sizing and FLIP puzzle animations.
 *
 * Layout rules:
 * - Portrait (9:16) content: spans 1 col × 2 rows (tall tile)
 * - Landscape (16:9) content: spans 2 cols × 1 row (wide tile)
 * - Square (1:1) content: spans 1 col × 1 row
 *
 * Uses CSS grid-auto-flow: dense to pack tiles tightly like a puzzle.
 * FLIP animation slides tiles from old to new position on reorder.
 */
export function PulseGrid({ config }: PulseGridProps) {
  const items = usePulseStore((s) => s.items);
  const tipPopups = usePulseStore((s) => s.tipPopups);
  const playingIds = usePulsePlayback();
  const containerRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(config.columns || 6);

  const cellRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const preRenderRectsRef = useRef<Map<string, DOMRect>>(new Map());
  const animationPendingRef = useRef(false);
  const hasMountedRef = useRef(false);

  // Auto-calculate columns from container width.
  useEffect(() => {
    if (config.columns > 0) {
      setColumns(config.columns);
      return;
    }
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? el.clientWidth;
      const cols = Math.max(3, Math.floor(width / MIN_CELL_WIDTH));
      setColumns(cols);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [config.columns]);

  // Snapshot positions BEFORE React re-renders with new order.
  useEffect(() => {
    const unsub = usePulseStore.subscribe((state, prevState) => {
      if (state.items === prevState.items) return;
      const rects = new Map<string, DOMRect>();
      cellRefs.current.forEach((el, id) => {
        rects.set(id, el.getBoundingClientRect());
      });
      preRenderRectsRef.current = rects;
      animationPendingRef.current = true;
    });
    return unsub;
  }, []);

  // Animate after React commits new order.
  const orderKey = items.map((i) => i.contentId).join(',');

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (!animationPendingRef.current) return;
    animationPendingRef.current = false;

    const prev = preRenderRectsRef.current;
    if (prev.size === 0) return;

    requestAnimationFrame(() => {
      cellRefs.current.forEach((el, id) => {
        const oldRect = prev.get(id);
        if (!oldRect) {
          el.style.opacity = '0';
          el.style.transition = 'opacity 400ms ease-out';
          requestAnimationFrame(() => { el.style.opacity = '1'; });
          setTimeout(() => { el.style.transition = ''; el.style.opacity = ''; }, 450);
          return;
        }

        const newRect = el.getBoundingClientRect();
        const dx = oldRect.left - newRect.left;
        const dy = oldRect.top - newRect.top;

        if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;

        el.style.transform = `translate(${dx}px, ${dy}px)`;
        el.style.transition = 'none';
        el.style.zIndex = '10';

        requestAnimationFrame(() => {
          el.style.transition = `transform ${config.transitionDurationMs}ms cubic-bezier(0.4, 0.0, 0.2, 1)`;
          el.style.transform = 'translate(0, 0)';

          const cleanup = () => {
            el.style.transform = '';
            el.style.zIndex = '';
            el.style.transition = '';
          };
          el.addEventListener('transitionend', cleanup, { once: true });
          setTimeout(cleanup, config.transitionDurationMs + 50);
        });
      });
    });
  }, [orderKey, config.transitionDurationMs]);

  // Tip popups by content.
  const popupsByContent = new Map<string, typeof tipPopups>();
  for (const popup of tipPopups) {
    const existing = popupsByContent.get(popup.contentId) ?? [];
    existing.push(popup);
    popupsByContent.set(popup.contentId, existing);
  }

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden"
      style={{
        display: 'grid',
        // Each column is 1fr wide. Row height = column width (square base unit).
        // Portrait spans 2 rows (≈9:16), landscape spans 2 cols (≈16:9).
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridAutoRows: `calc((100vw - ${config.gap * (columns - 1)}px) / ${columns})`,
        gridAutoFlow: 'dense',
        gap: `${config.gap}px`,
      }}
    >
      {items.map((item) => (
        <PulseCell
          key={item.contentId}
          item={item}
          isPlaying={playingIds.has(item.contentId)}
          tipPopups={popupsByContent.get(item.contentId)}
          ref={(el: HTMLDivElement | null) => {
            if (el) cellRefs.current.set(item.contentId, el);
          }}
          style={getCellStyle(item)}
        />
      ))}
    </div>
  );
}

/** Compute grid span style based on content orientation. */
function getCellStyle(item: PulseContentItem): React.CSSProperties {
  switch (item.orientation) {
    case 'portrait':
      // Tall tile: 1 column wide, 2 rows tall.
      return { gridRow: 'span 2' };
    case 'landscape':
      // Wide tile: 2 columns wide, 1 row tall.
      return { gridColumn: 'span 2' };
    case 'square':
    default:
      // Standard 1×1 tile.
      return {};
  }
}
